import { type FlightItineraryApi } from "@/types/flights/flight-itinerary-api"

import { nowMillis } from "./date-utils"
import {
  FLIGHT_SEARCH_BACK_STATE_STORE,
  idbDelete,
  idbGet,
  idbPut,
} from "./ddb"
import { decodeData, encodeData } from "./ddb-utils"

const MAX_AGE_MS = 30 * 60 * 1000

export interface FlightBackStateEntry {
  key: string
  contractItinerary: string
  timestamp: number
  expireAt: number
}

/**
 * Build a cache key from the itinerary's slice hash_keys.
 * This uniquely identifies the specific flight combination,
 * so different options within the same chat get their own cache entry.
 */
function buildCacheKey(itinerary: FlightItineraryApi): string {
  return itinerary.flight_slices.map((s) => s.hash_key).join("_")
}

export async function cacheFlightSearchBackState(
  contractItinerary: FlightItineraryApi
): Promise<void> {
  try {
    const now = nowMillis()
    await idbPut<FlightBackStateEntry>(FLIGHT_SEARCH_BACK_STATE_STORE, {
      key: buildCacheKey(contractItinerary),
      contractItinerary: encodeData(contractItinerary),
      timestamp: now,
      expireAt: now + MAX_AGE_MS,
    })
  } catch (e) {
    // non-critical cache write — silent fail
  }
}

export async function getCachedFlightSearchBackState(
  selectedOptionKeys: string[]
): Promise<FlightItineraryApi | null> {
  if (selectedOptionKeys.length === 0) return null
  try {
    const cacheKey = selectedOptionKeys.join("_")
    const entry = await idbGet<FlightBackStateEntry>(
      FLIGHT_SEARCH_BACK_STATE_STORE,
      cacheKey
    )
    if (!entry) return null
    if (nowMillis() > entry.expireAt) {
      await clearFlightSearchBackState(selectedOptionKeys)
      return null
    }
    return decodeData<FlightItineraryApi>(entry.contractItinerary) ?? null
  } catch (e) {
    return null
  }
}

export async function clearFlightSearchBackState(
  selectedOptionKeys: string[]
): Promise<void> {
  if (selectedOptionKeys.length === 0) return
  try {
    await idbDelete(
      FLIGHT_SEARCH_BACK_STATE_STORE,
      selectedOptionKeys.join("_")
    )
  } catch (e) {
    // silent fail
  }
}
